import { getCurrentAuthUser, onAuthStateChanged } from '../firebaseAuth';

const DEFAULT_COMPANY_PORTAL_PATH = '/dashboard?section=empresa';
const COMPANY_PORTAL_STORAGE_KEY = 'flotte:companyPortalContext';

const slugFields = [
  'slug',
  'companySlug',
  'company_slug',
  'companyProfileSlug',
  'company_profile_slug',
  'companyPortalSlug',
  'portalSlug',
];

const nestedCompanyKeys = [
  'company',
  'empresa',
  'profile',
  'companyProfile',
  'companyInfo',
  'values',
  'data',
  'expand',
];

const POCKETBASE_ID_PATTERN = /^[a-z0-9]{15}$/;

let cachedAuthUser = getCurrentAuthUser();
onAuthStateChanged(user => {
  cachedAuthUser = user;
});

const getCurrentUserId = () => {
  const authModel = cachedAuthUser;
  return typeof authModel?.id === 'string' ? authModel.id : '';
};

const safeTrim = value => {
  if (typeof value === 'number') {
    return String(value);
  }
  return typeof value === 'string' ? value.trim() : '';
};

const resolveStringCandidate = value => {
  const trimmed = safeTrim(value);
  if (!trimmed) {
    return { slug: '', fallback: '' };
  }
  if (POCKETBASE_ID_PATTERN.test(trimmed)) {
    return { slug: '', fallback: trimmed };
  }
  return { slug: trimmed, fallback: trimmed };
};

const extractCompanySlugCandidate = source => {
  if (!source) return { slug: '', fallback: '' };
  if (typeof source === 'string' || typeof source === 'number') {
    return resolveStringCandidate(source);
  }
  if (Array.isArray(source)) {
    let fallback = '';
    for (const item of source) {
      const candidate = extractCompanySlugCandidate(item);
      if (candidate.slug) {
        return candidate;
      }
      if (!fallback && candidate.fallback) {
        fallback = candidate.fallback;
      }
    }
    return { slug: '', fallback };
  }
  if (typeof source !== 'object') {
    return { slug: '', fallback: '' };
  }

  let fallback = '';
  for (const field of slugFields) {
    if (!Object.prototype.hasOwnProperty.call(source, field)) continue;
    const candidate = resolveStringCandidate(source[field]);
    if (candidate.slug) {
      return candidate;
    }
    if (!fallback && candidate.fallback) {
      fallback = candidate.fallback;
    }
  }

  for (const key of nestedCompanyKeys) {
    if (!Object.prototype.hasOwnProperty.call(source, key)) continue;
    const candidate = extractCompanySlugCandidate(source[key]);
    if (candidate.slug) {
      return candidate;
    }
    if (!fallback && candidate.fallback) {
      fallback = candidate.fallback;
    }
  }

  return { slug: '', fallback };
};

const looksLikeUserRecord = value => {
  if (!value || typeof value !== 'object') return false;
  if (value.collectionName === 'users') return true;
  return (
    typeof value.email === 'string' ||
    typeof value.username === 'string' ||
    typeof value.role === 'string' ||
    typeof value.phoneNumber === 'string'
  );
};

const extractUserId = source => {
  if (!source) return '';
  if (typeof source === 'string') {
    return source.trim();
  }
  if (Array.isArray(source)) {
    for (const item of source) {
      const id = extractUserId(item);
      if (id) return id;
    }
    return '';
  }
  if (typeof source !== 'object') {
    return '';
  }
  if (looksLikeUserRecord(source) && typeof source.id === 'string' && source.id.trim()) {
    return source.id.trim();
  }
  if (typeof source.userId === 'string' && source.userId.trim()) {
    return source.userId.trim();
  }
  if (looksLikeUserRecord(source.user) && typeof source.user.id === 'string' && source.user.id.trim()) {
    return source.user.id.trim();
  }
  return '';
};

const readStoredContext = () => {
  if (typeof window === 'undefined' || !window.sessionStorage) {
    return null;
  }
  try {
    const raw = window.sessionStorage.getItem(COMPANY_PORTAL_STORAGE_KEY);
    if (!raw) return null;
    const payload = JSON.parse(raw);
    const slug = safeTrim(payload?.slug);
    if (!slug) return null;
    const userId = safeTrim(payload?.userId);
    return { slug, userId };
  } catch (error) {
    console.warn('No se pudo leer el contexto del portal de empresa almacenado.', error);
    try {
      window.sessionStorage.removeItem(COMPANY_PORTAL_STORAGE_KEY);
    } catch {
      // ignore storage cleanup errors
    }
    return null;
  }
};

const writeStoredContext = (slug, userId) => {
  if (typeof window === 'undefined' || !window.sessionStorage) {
    return;
  }
  if (!slug) {
    window.sessionStorage.removeItem(COMPANY_PORTAL_STORAGE_KEY);
    return;
  }
  try {
    const payload = userId ? { slug, userId } : { slug };
    window.sessionStorage.setItem(COMPANY_PORTAL_STORAGE_KEY, JSON.stringify(payload));
  } catch (error) {
    console.warn('No se pudo guardar el contexto del portal de empresa.', error);
  }
};

export const clearCompanyPortalSlug = () => {
  if (typeof window === 'undefined' || !window.sessionStorage) {
    return;
  }
  try {
    window.sessionStorage.removeItem(COMPANY_PORTAL_STORAGE_KEY);
  } catch (error) {
    console.warn('No se pudo limpiar el contexto del portal de empresa.', error);
  }
};

const rememberSlugForUser = (slug, userId) => {
  if (!slug) {
    clearCompanyPortalSlug();
    return '';
  }
  const resolvedUserId = safeTrim(userId) || getCurrentUserId();
  writeStoredContext(slug, resolvedUserId);
  return slug;
};

export const rememberCompanyPortalSlug = (companySource, userSource) => {
  const candidate = extractCompanySlugCandidate(companySource);
  const slug = candidate.slug;
  if (!slug) {
    return '';
  }
  const resolvedUserId = extractUserId(userSource) || extractUserId(companySource);
  rememberSlugForUser(slug, resolvedUserId);
  return slug;
};

const resolveStoredSlug = () => {
  const stored = readStoredContext();
  if (!stored) return '';
  const currentUserId = getCurrentUserId();
  if (stored.userId && currentUserId && stored.userId !== currentUserId) {
    return '';
  }
  return stored.slug || '';
};

export const getCompanyPortalSlug = source => {
  const sourceCandidate = extractCompanySlugCandidate(source);
  if (sourceCandidate.slug) {
    const userId = extractUserId(source);
    rememberSlugForUser(sourceCandidate.slug, userId);
    return sourceCandidate.slug;
  }

  const authModel = cachedAuthUser || null;
  const authCandidate = extractCompanySlugCandidate(authModel);
  if (authCandidate.slug) {
    const userId = extractUserId(authModel);
    rememberSlugForUser(authCandidate.slug, userId);
    return authCandidate.slug;
  }

  const storedSlug = resolveStoredSlug();
  if (storedSlug) {
    return storedSlug;
  }

  return sourceCandidate.fallback || authCandidate.fallback || '';
};

export const getCompanyPortalPath = source => {
  const slug = getCompanyPortalSlug(source);
  if (slug) {
    return `/empresa/${encodeURIComponent(slug)}`;
  }
  return DEFAULT_COMPANY_PORTAL_PATH;
};
